import React from "react";
import { makeStyles } from "@mui/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import StarsIcon from '@mui/icons-material/Stars';
import Tooltip from '@mui/material/Tooltip';
import _ from "lodash";
import Rating3Badge from '../common/Rating3Badge';
import ImpactRatingBadge from '../common/ImpactRatingBadge';

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
  tableContainer: {
    background: "rgba(12, 12, 13, 0.9) !important",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "18px !important",
    boxShadow: "none !important",
  },
  headRow: {
    backgroundColor: "rgba(255,255,255,0.04)",
  },
  bestPlayerRow: {
    backgroundColor: "rgba(232, 163, 61, 0.18)",
    '&:hover': {
      backgroundColor: 'rgba(232, 163, 61, 0.24)',
    },
  },
  playerRow: {
    backgroundColor: "rgba(255,255,255,0.01)",
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.05)',
    },
  },
  bestPlayerCell: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "0.3rem",
  },
  tooltipContent: {
    backgroundColor: "#1C1E20",
    padding: "0.3rem 0.5rem",
    borderRadius: "4px",
    border: "1px solid #6c757d",
    color: "#ffffff",
    fontSize: "12px",
    marginBottom: "0.5rem",
  },
  tableHeadCell: {
    color: "#ffffff !important",
    borderBottom: "1px solid rgba(255,255,255,0.08) !important",
    fontWeight: "700 !important",
    whiteSpace: "nowrap",
  },
  tableCell: {
    color: "rgba(255,255,255,0.88) !important",
    borderBottom: "1px solid rgba(255,255,255,0.06) !important",
    whiteSpace: "nowrap",
  },
});

const getPlayerSortScore = (player) => {
  if (player?.rating3 != null) return player.rating3;
  if (!player?.stats) return 0;
  const { damage, kills, assists, deaths, adr, mvp } = player.stats;
  return damage * 3.5 + kills * 4 + assists * 2 + adr * 0.5 + mvp * 3 - deaths * 1.5;
};

const getBestPlayer = (players) => {
  if (!players.length) return null;
  return players.reduce((best, current) =>
    getPlayerSortScore(current) > getPlayerSortScore(best) ? current : best
  );
};

export default function Scoreboard({ players }) {
  const classes = useStyles();

  const bestPlayer = getBestPlayer(players);
  const otherPlayers = bestPlayer ? players.filter(p => p.steamId !== bestPlayer.steamId) : players;

  const sortedOtherPlayers = _.orderBy(
    otherPlayers,
    ['stats.kills', 'stats.damage', 'stats.assists', 'stats.adr'],
    ['desc', 'desc', 'desc', 'desc']
  );

  const finalSortedPlayers = bestPlayer ? [bestPlayer, ...sortedOtherPlayers] : sortedOtherPlayers;

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow className={classes.headRow}>
            <TableCell className={classes.tableHeadCell}>Player</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>Rating</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>Impact</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>Kills</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>Assists</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>Deaths</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>Damage</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>K/D Ratio</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>Headshots</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>ADR</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>MVPs</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>HS %</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>3K</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>4K</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
        {finalSortedPlayers.map((p) => {
            const isBestPlayer = p.steamId === bestPlayer?.steamId;

            return (
              <TableRow
                key={p.steamId}
                className={isBestPlayer ? classes.bestPlayerRow : classes.playerRow}
              >
                <TableCell
                  component="th"
                  scope="row"
                  className={`${classes.tableCell} ${isBestPlayer ? classes.bestPlayerCell : ""}`}
                >
                  {p.name}{isBestPlayer ? <Tooltip title="Team's MVP!" placement="top" classes={{
                    tooltip: classes.tooltipContent, 
                  }}><StarsIcon /></Tooltip> : ""}
                </TableCell>
                <TableCell align="center" className={classes.tableCell}>
                  <Rating3Badge value={p.rating3} subRatings={p.rating3Sub} size="small" />
                </TableCell>
                <TableCell align="center" className={classes.tableCell}>
                  <ImpactRatingBadge value={p.impactRating?.current ?? p.impactRating} components={p.impactRating?.components || null} size="small" />
                </TableCell>
                <TableCell align="center" className={classes.tableCell}>{p.stats.kills}</TableCell>
                <TableCell align="center" className={classes.tableCell}>{p.stats.assists}</TableCell>
                <TableCell align="center" className={classes.tableCell}>{p.stats.deaths}</TableCell>
                <TableCell align="center" className={classes.tableCell}>{p.stats.damage}</TableCell>
                <TableCell align="center" className={classes.tableCell}>{p.stats.kdRatio}</TableCell>
                <TableCell align="center" className={classes.tableCell}>{p.stats.headshot_kills}</TableCell>
                <TableCell align="center" className={classes.tableCell}>{p.stats.adr}</TableCell>
                <TableCell align="center" className={classes.tableCell}>{p.stats.mvp}</TableCell>
                <TableCell align="center" className={classes.tableCell}>{p.stats.hs_percent}%</TableCell>
                <TableCell align="center" className={classes.tableCell}>{p.stats["3k"]}</TableCell>
                <TableCell align="center" className={classes.tableCell}>{p.stats["4k"]}</TableCell> 
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
